import { MenuInitializer } from './MenuInitializer';

export class SmartNavHover {
	private nav: HTMLElement;
	private activeItem: HTMLElement | null = null;
	private mouseHistory: { x: number; y: number }[] = [];
	private readonly MAX_HISTORY = 5;
	private delayTimeout: number | null = null;
	private delayDuration: number = 500; // delay before item gets activated when iside triangle (ms)
	private allNavItems: HTMLElement[];

	constructor(navElement: HTMLElement) {
		if (!navElement) throw new Error('navElement not found.');
		this.nav = navElement;
		this.allNavItems = Array.from(this.nav.querySelectorAll<HTMLElement>('[data-hover-nav-item]'));

		this.init();
	}

	/**
	 * Initializes the eventlisteners needed for the smart nav
	 *
	 * @returns void
	 */
	private init(): void {
		this.nav.addEventListener('mousemove', this.trackMouse.bind(this));
		this.nav.addEventListener('mouseleave', this.onNavLeave.bind(this));

		this.allNavItems.forEach((item) => {
			const linkElement = item.querySelector<HTMLElement>('&>a');
			if (linkElement === null) return;
			linkElement.addEventListener('mouseenter', (e) => this.onItemHover(item));
		});
	}

	/**
	 * Tracks the mouse movement
	 *
	 * @param {MouseEvent} e MouseEvent from the eventlistener
	 *
	 * @returns void
	 */
	private trackMouse(e: MouseEvent): void {
		this.mouseHistory.push({ x: e.pageX, y: e.pageY });
		if (this.mouseHistory.length > this.MAX_HISTORY) {
			this.mouseHistory.shift();
		}
	}

	/**
	 * Conrols the functionality on hovering a menu item
	 *
	 * @param {HTMLElement} item Hovered menu item
	 *
	 * @returns void
	 */
	private onItemHover(item: HTMLElement): void {
		if (this.activeItem === item) return;

		const submenu = this.activeItem?.querySelector<HTMLElement>('.sub-nav-container');
		if (!submenu || this.mouseHistory.length < 2) {
			this.activateItem(item);
			return;
		}

		const submenuRect = submenu.getBoundingClientRect();
		const topLeft = { x: submenuRect.left, y: submenuRect.top };
		const bottomLeft = { x: submenuRect.left, y: submenuRect.bottom };

		const lastPos = this.mouseHistory[this.mouseHistory.length - 2];
		const currentPos = this.mouseHistory[this.mouseHistory.length - 1];

		// Skip delay if movement is leftward
		const deltaX = currentPos.x - lastPos.x;
		if (deltaX < 0) {
			this.activateItem(item);
			return;
		}

		if (this.isPointInTriangle(currentPos, lastPos, topLeft, bottomLeft)) {
			this.delayActivation(item);
		} else {
			this.activateItem(item);
		}
	}

	/**
	 * Delaying the activation of a menu item
	 *
	 * @param {HTMLElement} item Hovered menu item
	 *
	 * @returns void
	 */
	private delayActivation(item: HTMLElement): void {
		if (this.delayTimeout) clearTimeout(this.delayTimeout);

		this.delayTimeout = window.setTimeout(() => {
			const deepestHovered = this.getDeepestHoveredElement();

			if (deepestHovered && item.contains(deepestHovered)) {
				this.activateItem(item);
			}
			this.delayTimeout = null;
		}, this.delayDuration);
	}

	/**
	 * Returns the element you are currently hovering
	 *
	 * @returns HTMLElement | null
	 */
	private getDeepestHoveredElement(): HTMLElement | null {
		let el: HTMLElement | null = document.body;
		let deepest: HTMLElement | null = null;

		while (el) {
			const hoveredChildren = Array.from(el.children).filter((child) =>
				(child as HTMLElement).matches(':hover')
			) as HTMLElement[];

			if (hoveredChildren.length === 0) break;

			el = hoveredChildren[0];
			deepest = el;
		}

		return deepest;
	}

	/**
	 * Activaes the given menu item and all parent items
	 *
	 * @param {HTMLElement} item Hovered menu item
	 *
	 * @returns void
	 */
	private activateItem(item: HTMLElement): void {
		this.nav.querySelectorAll('.active').forEach((el) => el.classList.remove('active'));

		let current: HTMLElement | null = item;
		while (current && current !== this.nav) {
			if (current.matches('li')) {
				current.classList.add('active');
			}
			current = current.parentElement;
		}

		this.activeItem = item;

		const toggledSubMenu = document.querySelector<HTMLUListElement>('.primary-sub-nav-container.open');
		if (toggledSubMenu === null) return;
		MenuInitializer.calculateSubMenuHeight(toggledSubMenu);
	}

	/**
	 * Detects when leaving the entire navigation element and removes all remaining active classes
	 *
	 * @returns void
	 */
	private onNavLeave(): void {
		this.allNavItems.forEach((item) => {
			item.classList.remove('active');
			this.activeItem = null;
		});
	}

	/**
	 * Detects when leaving the entire navigation element and removes all remaining active classes
	 *
	 * @param p Current mouse position
	 * @param a Last mouse position
	 * @param b Top left point of the submenu
	 * @param c Bottom left point of the submenu
	 *
	 * @returns boolean
	 */
	private isPointInTriangle(
		p: { x: number; y: number },
		a: { x: number; y: number },
		b: { x: number; y: number },
		c: { x: number; y: number }
	): boolean {
		const area = (v1: any, v2: any, v3: any) =>
			Math.abs((v1.x * (v2.y - v3.y) + v2.x * (v3.y - v1.y) + v3.x * (v1.y - v2.y)) / 2);

		const total = area(a, b, c);
		const a1 = area(p, b, c);
		const a2 = area(a, p, c);
		const a3 = area(a, b, p);

		return Math.abs(total - (a1 + a2 + a3)) < 0.5;
	}
}
